<template>
  <div>
    <b-form-group
      :id="$id('shortContentHeadlineFieldset')"
      label-cols="2"
      label="Title"
      label-class="pt-0"
      :label-for="$id('shortContentHeadline')">
      <template
        v-if="isInsider"
        #description>
        Title of the item (appears on the Android webview header only)
      </template>
      <b-form-input
        :id="$id('shortContentHeadline')"
        :value="title"
        name="title"
        @input.native="emitFieldUpdate" />
    </b-form-group>

    <b-form-group
      :id="$id('shortContentUrlFieldset')"
      label-cols="2"
      label="URL"
      label-class="pt-0"
      :label-for="$id('shortContentUrl')"
      description="Item URL">
      <b-form-input
        :id="$id('shortContentUrl')"
        :value="url"
        name="url"
        @input.native="emitFieldUpdate" />
    </b-form-group>

    <b-form-group
      :id="$id('shortContentLaunchExternallyFieldset')"
      label-cols="2"
      label="Launch Externally?"
      label-class="pt-0"
      :label-for="$id('shortContentLaunchExternally')"
      description="Should the item launch outside of the app?">
      <b-form-checkbox
        :id="$id('shortContentPopup')"
        :checked="popup"
        name="popup"
        @input.native="emitFieldUpdate($event, true, false)" />
    </b-form-group>

    <b-form-group
      v-if="!shouldHideImageField"
      :id="$id('shortContentImageFieldset')"
      label-cols="2"
      label="Image"
      label-class="pt-0"
      :label-for="$id('shortContentImageUpload')"
      description="The image attached to the short content">
      <b-input-group>
        <b-form-input
          :id="$id('shortContentImageUpload')"
          :value="imageUrl"
          name="imageUrl"
          type="url"
          trim
          @input.native="emitFieldUpdate" />
        <b-input-group-append>
          <b-btn
            variant="outline-primary"
            @click.prevent="$refs.assetUploadModal.show()">
            <i class="fas fa-plus" />
          </b-btn>
        </b-input-group-append>
      </b-input-group>
      <template
        v-if="!isInsider"
        #description>
        NOTE: Images do not display for More From items on Android devices
      </template>
    </b-form-group>

    <b-form-group
      :id="$id('shortContentPlatformsFieldset')"
      label-cols="2"
      label="Platforms"
      label-class="pt-0"
      description="The platform(s) this short content should appear on">
      <b-form-checkbox-group
        v-model="platforms"
        :options="[{text: 'iOS', value: 'ios'}, {text: 'Android', value: 'android'}]"
        switches
        stacked
        name="platforms[]"
        @change="emitFieldUpdate({target: {name: 'platforms', value: $event}})" />
    </b-form-group>

    <b-form-group
      :id="$id('shortContentDevicesFieldset')"
      label-cols="2"
      label="Devices"
      label-class="pt-0"
      description="The device(s) this short content should appear on">
      <b-form-checkbox-group
        v-model="devices"
        :options="[{text: 'Phone', value: 'phone'}, {text: 'Tablet', value: 'tablet'}]"
        switches
        stacked
        name="devices[]"
        @change="emitFieldUpdate({target: {name: 'devices', value: $event}})" />
    </b-form-group>

    <b-form-group
      :id="$id('shortContentLocalesFieldset')"
      label-cols="2"
      label="Locales"
      label-class="pt-0"
      description="The locale(s) this short content should appear on">
      <b-form-checkbox-group
        v-model="locales"
        :options="[{text: 'English', value: 'en'}, {text: 'Spanish', value: 'es'}, {text: 'Japanese', value: 'ja'}]"
        switches
        stacked
        name="locales[]"
        @change="emitFieldUpdate({target: {name: 'locales', value: $event}})" />
    </b-form-group>

    <b-modal
      id="asset-upload"
      ref="assetUploadModal"
      hide-header
      hide-footer
      size="lg">
      <asset-upload
        :post-action="`https://api.cloudinary.com/v1_1/${CLOUDINARY_ACCOUNT}/image/upload`"
        :existing-files="[]"
        :without-auth-header="true"
        :cloudinary="{}"
        :constraint="constraint"
        @asset-selected-response="onAssetSelect"
        @cancel="$refs.assetUploadModal.hide()" />
    </b-modal>
  </div>
</template>

<script>
import AssetUpload from '@/components/AssetUpload'

export default {
  name: 'ShortContent',
  components: {
    AssetUpload,
  },
  props: {
    id: {
      type: String,
      default: null,
    },
    title: {
      type: String,
      default: null,
    },
    url: {
      type: String,
      default: null,
    },
    popup: {
      type: Boolean,
      default: false,
    },
    imageUrl: {
      type: String,
      default: null,
    },
    hbImages: {
      type: Object,
      default: null,
    },
    platforms: {
      type: Array,
      default: () => [],
    },
    devices: {
      type: Array,
      default: () => [],
    },
    locales: {
      type: Array,
      default: () => [],
    },
    isInsider: {
      type: Boolean,
      default: false,
    },
    constraint: {
      type: Object,
      required: true,
    },
  },
  computed: {
    CLOUDINARY_ACCOUNT() {
      return process.env.VUE_APP_CLOUDINARY_ACCOUNT
    },
    shouldHideImageField() {
// We don't want to expose the image field for "More From" Android-only items.
      return !this.isInsider && this.platforms.length === 1 && this.platforms[0] === "android"
    },
  },
  methods: {
    onAssetSelect(response) {
      this.$refs.assetUploadModal.hide()

      if (response.url) {
        this.emitFieldUpdates([
          {
            target: {
              name: 'imageUrl',
              value: encodeURI(response.url),
            },
          },
          {
            target: {
              name: 'imagePublicId',
              value: response.public_id,
            },
          },
        ])
      } else {
        console.warn('Ignoring invalid asset selection')
      }
    },
    emitFieldUpdate(event, truthy, falsy) {
      const value = (() => {
        if (event.target.type === 'checkbox') {
          return event.target.checked ? truthy : falsy
        }

        return event.target.value
      })()

      this.$emit("field-update", {
        id: this.id,
        field: event.target.name,
        value,
      })
    },
    emitFieldUpdates(events) {
      const emitFieldUpdate = this.emitFieldUpdate

      events.forEach((event) => emitFieldUpdate(event))
    },
  },
}
</script>

<style scoped>

</style>
